import {defineToolRegistryManifest} from '../../types/base-tool-types';
import {z} from 'zod';
import dedent from 'dedent';

const toolName = 'extract-site-styles' as const;

const argumentZod = z.object({
  focus: z
    .array(z.enum(['typography', 'colors', 'radii', 'spacing', 'links']))
    .optional(),
  maxSamples: z.number().optional(),
});

export type ExtractSiteStylesArgumentType = z.infer<typeof argumentZod>;

export const extractSiteStylesTool = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Capture site styles',
  family: 'site',
  prettyErrors: {
    fallback: 'Unable to extract site styles.',
    patterns: [
      {
        match: 'preview not available',
        message: 'Site preview is not available.',
      },
      {
        match: 'extraction failed',
        message: 'Style extraction failed.',
      },
    ],
  },
  llmTool: {
    name: toolName,
    description: dedent`
      Extracts brand styles from the current site by analyzing its rendered page.

      Results are stored per site revision and automatically refreshed when the
      site changes.

      Opens the site in a headless browser, samples computed styles from DOM elements,
      extracts CSS variables, and infers design tokens.

      Returns typography, colors, border radii, spacing, and other design tokens
      inferred from the rendered page. When inferred values are compatible with Webflow
      variables, returns their CSS var() references for code components to use.`,
    inputSchema: argumentZod,
  },
  type: 'server',
  permission: [{resource: 'SiteStyleBlock', action: 'read'}],
  confirmBeforeExecuting: false,
  isConcurrencySafe: () => true,
  isReadOnly: () => true,
  timeout: 45000,
  compaction: {
    prompt:
      'Summarize the extracted site styles. Preserve color values, font families, font sizes, spacing values, border radii, and all CSS var() references. Remove repetitive samples and verbose formatting.',
    staleAfterTurns: 5,
    supersededOnRecall: true,
  },
});
